// src/pages/SmartySaveMore.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

const CATEGORY_LIST = [
  { id: "vegetables", label: "Vegetables", emoji: "🥦", group: "Fresh & Perishables" },
  { id: "fruits_nuts", label: "Fruits & Nuts", emoji: "🍎", group: "Fresh & Perishables" },
  { id: "non_veg", label: "Meat / Fish / Eggs", emoji: "🍗", group: "Fresh & Perishables" },
  { id: "dairy", label: "Dairy Products", emoji: "🥛", group: "Fresh & Perishables" },
  { id: "bakery", label: "Bread & Bakery", emoji: "🍞", group: "Fresh & Perishables" },

  { id: "grocery", label: "General Grocery", emoji: "🛒", group: "Grocery & Staples" },
  { id: "spices", label: "Spices & Masalas", emoji: "🌶️", group: "Grocery & Staples" },
  { id: "oils_ghee", label: "Cooking Oils & Ghee", emoji: "🧴", group: "Grocery & Staples" },
  { id: "pulses", label: "Pulses / Lentils", emoji: "🥣", group: "Grocery & Staples" },
  { id: "rice_wheat_grains", label: "Rice / Wheat / Grains", emoji: "🌾", group: "Grocery & Staples" },

  { id: "snacks_sweets", label: "Snacks & Sweets", emoji: "🍪", group: "Snacks & Beverages" },
  { id: "tea_coffee", label: "Tea / Coffee", emoji: "☕", group: "Snacks & Beverages" },
  { id: "juices_drinks", label: "Juices & Beverages", emoji: "🧃", group: "Snacks & Beverages" },

  { id: "medicines", label: "Medicines", emoji: "💊", group: "Health / Toiletries / Cleaning" },
  { id: "cleaning_supplies", label: "Cleaning Supplies", emoji: "🧽", group: "Health / Toiletries / Cleaning" },
  { id: "toiletries", label: "Toiletries", emoji: "🧼", group: "Health / Toiletries / Cleaning" },
  { id: "cosmetics", label: "Cosmetics", emoji: "💄", group: "Health / Toiletries / Cleaning" },

  { id: "baby_food", label: "Baby Food & Formula", emoji: "👶", group: "Baby & Pet Items" },
  { id: "pet_food", label: "Pet Food", emoji: "🐶", group: "Baby & Pet Items" },

  { id: "frozen_foods", label: "Frozen Foods", emoji: "❄️", group: "Refrigerated & Packaged Foods" },
  { id: "ready_to_eat", label: "Ready-to-Eat Meals", emoji: "🍱", group: "Refrigerated & Packaged Foods" },
  { id: "canned_items", label: "Canned / Tinned Items", emoji: "🥫", group: "Refrigerated & Packaged Foods" },

  { id: "others", label: "Others", emoji: "📦", group: "Others" },
];

const SmartySaveMore = () => {
  const [activeTab, setActiveTab] = useState("story");
  const [selectedCategory, setSelectedCategory] = useState("vegetables");

  // Rough relative waste share for chart – purely illustrative
  const chartData = {
    labels: ["Vegetables", "Fruits", "Dairy", "Snacks", "Ready-to-eat"],
    datasets: [
      {
        label: "Typical Monthly Waste (₹)",
        data: [900, 250, 200, 300, 250],
        backgroundColor: [
          "rgba(74, 222, 128, 0.8)",   // green
          "rgba(250, 204, 21, 0.8)",   // yellow
          "rgba(96, 165, 250, 0.8)",   // blue
          "rgba(248, 113, 113, 0.8)",  // red
          "rgba(52, 211, 153, 0.8)",   // teal
        ],
        borderWidth: 0,
      },
    ],
  };

  const tabs = [
    { id: "story", label: "📖 Family Story" },
    { id: "categories", label: "📂 By Category" },
    { id: "india_world", label: "🇮🇳 India & 🌍 World" },
    { id: "actions", label: "✨ How Smarty Helps" },
  ];

  const selectedCatMeta = CATEGORY_LIST.find((c) => c.id === selectedCategory);

  return (
    <div className="min-h-screen bg-black text-teal-100 font-sans flex flex-col">
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-black border-b border-teal-800 px-4 py-3 flex items-center justify-between shadow-md">
        <h1 className="text-lg lg:text-xl font-bold text-yellow-300 flex items-center gap-2">
          🌟 Smarty – Save More
        </h1>
      </header>

      {/* TABS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 px-3 pt-3 pb-2 bg-zinc-900 border-b border-teal-900 text-xs">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-2 py-2 rounded-lg font-semibold transition-colors ${
              activeTab === t.id
                ? "bg-teal-700 text-white"
                : "bg-zinc-800 text-teal-300"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <main className="flex-1 overflow-y-auto px-4 py-4 space-y-5 pb-24 text-sm">
        {/* STORY TAB */}
        {activeTab === "story" && (
          <section>
            <h2 className="text-lg font-bold text-yellow-300 mb-2">
              👨‍👩‍👧‍👦 Sanjay’s Family – Hidden Wastage
            </h2>
            <p className="mb-2">
              Sanjay, his wife Meera, and their two children work hard and dream
              of saving more every month. But their budget always feels tight.
              Not because of big luxuries, but because of{" "}
              <span className="text-yellow-200 font-semibold">
                small waste in daily items
              </span>{" "}
              – especially food.
            </p>

            <p className="mb-3">
              Inside their kitchen, waste happens silently in almost every
              category:
            </p>

            <ul className="space-y-1 mb-3">
              <li>🥦 <b>Vegetables</b> forgotten at the back of the fridge</li>
              <li>🍎 <b>Fruits & Nuts</b> bought in bulk, spoiled in heat</li>
              <li>🥛 <b>Dairy</b> (milk, curd) going sour on busy days</li>
              <li>🍪 <b>Snacks & Sweets</b> opened and left half-eaten</li>
              <li>🍱 <b>Ready-to-Eat</b> packs expiring quietly</li>
            </ul>

            <p className="mb-3">
              Every month this “little waste” adds up to{" "}
              <span className="text-green-300 font-semibold">
                ₹2,000 – ₹4,000
              </span>{" "}
              lost, more greenhouse gases from rotting food, and arguments like:
            </p>

            <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3 mb-3">
              <p className="italic text-xs">
                “Itna sab kyon khareedte ho?” • “Tum fridge check kyun nahi karte?” •
                “Itna khana dustbin mein jaa raha hai!”
              </p>
            </div>

            <p className="mb-3">
              When they started using{" "}
              <span className="font-semibold text-teal-300">
                Smarty (Think5Whys)
              </span>
              , the app simply showed the{" "}
              <b>category-wise data</b> – how much was wasted in vegetables,
              fruits, dairy, snacks, ready-to-eat, etc.
            </p>

            <p className="text-green-300 font-semibold">
              Once they saw numbers, fights reduced, planning improved, and
              savings began.
            </p>
          </section>
        )}

        {/* CATEGORY TAB */}
        {activeTab === "categories" && (
          <section>
            <h2 className="text-lg font-bold text-yellow-300 mb-3">
              📂 Category-wise Waste & Opportunity
            </h2>

            {/* SELECT-LIKE STRIP (MOBILE FRIENDLY) */}
            <div className="mb-3">
              <label
                htmlFor="category"
                className="block mb-1 text-xs text-teal-300"
              >
                Focus Category:
              </label>
              <select
                id="category"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="mt-1 block w-full h-9 border border-teal-700 rounded px-2 py-1 bg-zinc-950 text-teal-100 text-xs"
              >
                <option value="">Select type</option>
                <optgroup label="Fresh & Perishables">
                  <option value="vegetables">Vegetables</option>
                  <option value="fruits_nuts">Fruits & Nuts</option>
                  <option value="non_veg">Meat / Fish / Eggs</option>
                  <option value="dairy">Dairy Products</option>
                  <option value="bakery">Bread & Bakery</option>
                </optgroup>
                <optgroup label="Grocery & Staples">
                  <option value="grocery">General Grocery</option>
                  <option value="spices">Spices & Masalas</option>
                  <option value="oils_ghee">Cooking Oils & Ghee</option>
                  <option value="pulses">Pulses / Lentils</option>
                  <option value="rice_wheat_grains">Rice / Wheat / Grains</option>
                </optgroup>
                <optgroup label="Snacks & Beverages">
                  <option value="snacks_sweets">Snacks & Sweets</option>
                  <option value="tea_coffee">Tea / Coffee</option>
                  <option value="juices_drinks">Juices & Beverages</option>
                </optgroup>
                <optgroup label="Health / Toiletries / Cleaning">
                  <option value="medicines">Medicines</option>
                  <option value="cleaning_supplies">Cleaning Supplies</option>
                  <option value="toiletries">Toiletries</option>
                  <option value="cosmetics">Cosmetics</option>
                </optgroup>
                <optgroup label="Baby & Pet Items">
                  <option value="baby_food">Baby Food & Formula</option>
                  <option value="pet_food">Pet Food</option>
                </optgroup>
                <optgroup label="Refrigerated & Packaged Foods">
                  <option value="frozen_foods">Frozen Foods</option>
                  <option value="ready_to_eat">Ready-to-Eat / Packaged Meals</option>
                  <option value="canned_items">Canned / Tinned Items</option>
                </optgroup>
                <optgroup label="Others">
                  <option value="others">Others</option>
                </optgroup>
              </select>
            </div>

            {selectedCatMeta && (
              <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-xs text-teal-400 uppercase tracking-wide">
                      {selectedCatMeta.group}
                    </p>
                    <h3 className="text-base font-semibold text-yellow-200 flex items-center gap-2">
                      <span>{selectedCatMeta.emoji}</span>
                      {selectedCatMeta.label}
                    </h3>
                  </div>
                </div>

                {/* You can tweak these texts per category if you want finer control */}
                <ul className="list-disc ml-4 text-xs space-y-1">
                  <li>
                    Typical monthly waste in this category can be{" "}
                    <span className="text-green-300 font-semibold">
                      ₹150 – ₹900
                    </span>{" "}
                    depending on family size.
                  </li>
                  <li>
                    Major reasons: overbuying, poor storage, forgetting items,
                    and unplanned eating out.
                  </li>
                  <li>
                    Wasted items mean wasted{" "}
                    <span className="text-yellow-200">
                      money + water + farmer effort
                    </span>
                    .
                  </li>
                  <li>
                    Rotting food releases{" "}
                    <span className="text-red-300 font-semibold">
                      methane
                    </span>
                    , a strong greenhouse gas.
                  </li>
                  <li>
                    With Smarty reminders and planning, waste here can drop by{" "}
                    <span className="text-green-300 font-semibold">30–60%</span>.
                  </li>
                </ul>
              </div>
            )}

            {/* QUICK OVERVIEW GRID */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CATEGORY_LIST.slice(0, 9).map((cat) => (
                <div
                  key={cat.id}
                  className="bg-zinc-900 border border-zinc-800 rounded-xl p-2 text-[11px]"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <span>{cat.emoji}</span>
                    <span className="font-semibold text-teal-100">
                      {cat.label}
                    </span>
                  </div>
                  <p className="text-[10px] text-teal-300">
                    Small daily waste here can cost{" "}
                    <span className="text-yellow-200 font-semibold">
                      ₹100–₹300/month
                    </span>{" "}
                    & add to greenhouse gases.
                  </p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* INDIA & WORLD TAB */}
        {activeTab === "india_world" && (
          <section>
            <h2 className="text-lg font-bold text-yellow-300 mb-2">
              🇮🇳 India & 🌍 World – Why Your Kitchen Matters
            </h2>

            <div className="mb-3 space-y-1">
              <p>🇮🇳 In India, crores of rupees of food are wasted every year.</p>
              <p>🏠 A big share of that waste comes from homes like Sanjay’s.</p>
              <p>
                🌫️ Rotting food in landfills releases methane –{" "}
                <b>28× stronger</b> than CO₂.
              </p>
            </div>

            <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3 mb-4 text-xs">
              <h3 className="text-teal-300 font-semibold mb-1">
                If 1 family reduces waste:
              </h3>
              <ul className="list-disc ml-4 space-y-1">
                <li>Save ₹1,500–₹3,000 per month.</li>
                <li>Send less food to dustbin → less methane.</li>
                <li>Free up money for fruits, milk, education, health.</li>
              </ul>
              <h3 className="text-teal-300 font-semibold mt-3 mb-1">
                If 1 crore families reduce waste:
              </h3>
              <ul className="list-disc ml-4 space-y-1">
                <li>Save thousands of crores of rupees.</li>
                <li>Reduce greenhouse gases noticeably.</li>
                <li>
                  Support farmers & reduce pressure on food supply and prices.
                </li>
              </ul>
            </div>

            <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3">
              <p className="text-xs mb-2 text-teal-200">
                A simple view of how different food categories eat your money:
              </p>
              <div className="mx-auto" style={{ width: "120px", height: "120px" }}>
                <Doughnut
                  data={chartData}
                  options={{
                    responsive: false,         
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false }
                    },
                    cutout: "60%",             
                  }}
                />
              </div>
            </div>
          </section>
        )}

        {/* ACTIONS TAB */}
        {activeTab === "actions" && (
          <section>
            <h2 className="text-lg font-bold text-yellow-300 mb-2">
              ✨ How Smarty Helps Your Family & Nation
            </h2>

            <p className="mb-3">
              Smarty under Think5Whys shows{" "}
              <b>clear, non-judgmental data</b> – so the family stops arguing,
              starts understanding, and then improving.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4 text-xs">
              <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3">
                <h3 className="font-semibold text-teal-300 mb-1">
                  🧮 Inside Your Home
                </h3>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Category-wise waste tracking (veg, fruits, dairy, etc.).</li>
                  <li>Expiry alerts for dairy, baby food, ready-to-eat.</li>
                  <li>“Don’t buy now” nudges when stock is enough.</li>
                  <li>Leftover usage suggestions to avoid staleness.</li>
                </ul>
              </div>

              <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3">
                <h3 className="font-semibold text-teal-300 mb-1">
                  💰 For Your Budget
                </h3>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Monthly report: money lost to waste vs money saved.</li>
                  <li>Helps set a “zero-waste target” per category.</li>
                  <li>Shows which habits give the highest savings.</li>
                </ul>
              </div>

              <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3">
                <h3 className="font-semibold text-teal-300 mb-1">
                  🌱 For Climate & Nation
                </h3>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Shows CO₂ / methane equivalent of your food waste.</li>
                  <li>Connects family actions to national savings.</li>
                  <li>Helps children see food as a valuable resource.</li>
                </ul>
              </div>

              <div className="bg-zinc-900 border border-teal-800 rounded-xl p-3">
                <h3 className="font-semibold text-teal-300 mb-1">
                  🧘 For Family Peace
                </h3>
                <ul className="list-disc ml-4 space-y-1">
                  <li>No blame, only numbers.</li>
                  <li>Everyone can see the same truth.</li>
                  <li>Arguments reduce, teamwork increases.</li>
                </ul>
              </div>
            </div>

            <p className="text-green-300 text-xs">
              Har category par thoda sa dhyaan –  
              ghar bachega, paisa bachega, desh bachega, aur dharti bhi. 🌎💚
            </p>
          </section>
        )}
      </main>

      {/* FOOTER */}
      <footer className="bg-black border-t border-teal-800 text-teal-300 py-4 text-center text-xs">
        © 2025 Think5Whys Global DeepTech Solutions.
        <div className="mt-2 flex justify-center gap-4">
          <Link to="/about-us" className="hover:text-teal-400">
            About
          </Link>
          <Link to="/privacy-policy" className="hover:text-teal-400">
            Privacy
          </Link>
          <Link to="/terms-of-use" className="hover:text-teal-400">
            Terms
          </Link>
          <Link to="/contact-us" className="hover:text-teal-400">
            Contact
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default SmartySaveMore;
